import { AppMode } from '@k-ucap/shared';
import { getFirebaseStatus } from '@/infrastructure/firebase/firebaseStatus.js';
import type { Repositories } from './RepositoryInterfaces.js';
import { createInMemoryUserRepository } from './InMemoryUserRepository.js';
import { createInMemoryProjectRepository } from './InMemoryProjectRepository.js';
import { createInMemoryProjectMemberRepository } from './InMemoryProjectMemberRepository.js';
import { createInMemoryMessageDefinitionRepository } from './InMemoryMessageDefinitionRepository.js';
import { createInMemoryMessageEventRepository } from './InMemoryMessageEventRepository.js';
import { createInMemoryScenarioRepository } from './InMemoryScenarioRepository.js';
import { createInMemoryVehicleRepository } from './InMemoryVehicleRepository.js';
import { createInMemoryRouteRepository } from './InMemoryRouteRepository.js';
import { createFirestoreUserRepository } from '@/infrastructure/firestore/FirestoreUserRepository.js';
import { createFirestoreProjectRepository } from '@/infrastructure/firestore/FirestoreProjectRepository.js';
import { createFirestoreProjectMemberRepository } from '@/infrastructure/firestore/FirestoreProjectMemberRepository.js';
import { createFirestoreMessageDefinitionRepository } from '@/infrastructure/firestore/FirestoreMessageDefinitionRepository.js';
import { createFirestoreMessageEventRepository } from '@/infrastructure/firestore/FirestoreMessageEventRepository.js';
import { createFirestoreScenarioRepository } from '@/infrastructure/firestore/FirestoreScenarioRepository.js';
import { createFirestoreVehicleRepository } from '@/infrastructure/firestore/FirestoreVehicleRepository.js';
import { createFirestoreRouteRepository } from '@/infrastructure/firestore/FirestoreRouteRepository.js';
import { createFirestoreSimulationTestRepository } from '@/infrastructure/firestore/FirestoreSimulationTestRepository.js';
import { createInMemorySimulationTestRepository } from './InMemorySimulationTestRepository.js';
import { createFirestoreSimulationRunRepository } from '@/infrastructure/firestore/FirestoreSimulationRunRepository.js';
import { createInMemorySimulationRunRepository } from './InMemorySimulationRunRepository.js';
import { createFirestoreReportRepository } from '@/infrastructure/firestore/FirestoreReportRepository.js';
import { createInMemoryReportRepository } from './InMemoryReportRepository.js';
import { createFirestoreConnectionProfileRepository } from '@/infrastructure/firestore/FirestoreConnectionProfileRepository.js';
import { createInMemoryConnectionProfileRepository } from './InMemoryConnectionProfileRepository.js';
import { createFirestoreTestSuiteRepository } from '@/infrastructure/firestore/FirestoreTestSuiteRepository.js';
import { createInMemoryTestSuiteRepository } from './InMemoryTestSuiteRepository.js';
import { createFirestoreTestCaseRepository } from '@/infrastructure/firestore/FirestoreTestCaseRepository.js';
import { createInMemoryTestCaseRepository } from './InMemoryTestCaseRepository.js';
import { createFirestoreSuiteResultRepository } from '@/infrastructure/firestore/FirestoreSuiteResultRepository.js';
import { createInMemorySuiteResultRepository } from './InMemorySuiteResultRepository.js';
import { createFirestoreFavoriteRepository } from '@/infrastructure/firestore/FirestoreFavoriteRepository.js';
import { createInMemoryFavoriteRepository } from './InMemoryFavoriteRepository.js';

import { createFirestoreSessionRepository } from '@/infrastructure/firestore/FirestoreSessionRepository.js';
import { createInMemorySessionRepository } from './InMemorySessionRepository.js';
import { createFirestoreSessionEvidenceRepository } from '@/infrastructure/firestore/FirestoreSessionEvidenceRepository.js';
import { createInMemorySessionEvidenceRepository } from './InMemorySessionEvidenceRepository.js';
import { createFirestoreMessageTemplateRepository } from '@/infrastructure/firestore/FirestoreMessageTemplateRepository.js';
import { createInMemoryMessageTemplateRepository } from './InMemoryMessageTemplateRepository.js';
import { createFirestoreParameterSetRepository } from '@/infrastructure/firestore/FirestoreParameterSetRepository.js';
import { createInMemoryParameterSetRepository } from './InMemoryParameterSetRepository.js';
import { createFirestoreDatasetRepository } from '@/infrastructure/firestore/FirestoreDatasetRepository.js';
import { createInMemoryDatasetRepository } from './InMemoryDatasetRepository.js';

import { seedMockData } from '@/mock/data.js';

export type RepositoryMode = 'MOCK' | 'FIREBASE' | 'CONFIG_ERROR';

export const getRepositoryMode = (): RepositoryMode => {
  if (import.meta.env?.VITE_FORCE_MOCK_MODE === 'true') return 'MOCK';
  const status = getFirebaseStatus();
  return status.mode === AppMode.FIREBASE ? 'FIREBASE' : status.mode === AppMode.MOCK ? 'MOCK' : 'CONFIG_ERROR';
};

export const createRepositories = (mode?: RepositoryMode): Repositories => {
  const selectedMode = mode ?? getRepositoryMode();
  if (selectedMode === 'FIREBASE') {
    return {
      userRepository: createFirestoreUserRepository(),
      projectRepository: createFirestoreProjectRepository(),
      projectMemberRepository: createFirestoreProjectMemberRepository(),
      messageDefinitionRepository: createFirestoreMessageDefinitionRepository(),
      messageEventRepository: createFirestoreMessageEventRepository(),
      scenarioRepository: createFirestoreScenarioRepository(),
      simulationTestRepository: createFirestoreSimulationTestRepository(),
      simulationRunRepository: createFirestoreSimulationRunRepository(),
      testSuiteRepository: createFirestoreTestSuiteRepository(),
      testCaseRepository: createFirestoreTestCaseRepository(),
      suiteResultRepository: createFirestoreSuiteResultRepository(),
      connectionProfileRepository: createFirestoreConnectionProfileRepository(),
      reportRepository: createFirestoreReportRepository(),
      favoriteRepository: createFirestoreFavoriteRepository(),
      vehicleRepository: createFirestoreVehicleRepository(),
      routeRepository: createFirestoreRouteRepository(),
      sessionRepository: createFirestoreSessionRepository(),
      sessionEvidenceRepository: createFirestoreSessionEvidenceRepository(),
      messageTemplateRepository: createFirestoreMessageTemplateRepository(),
      parameterSetRepository: createFirestoreParameterSetRepository(),
      datasetRepository: createFirestoreDatasetRepository(),
    };
  }
  const projectRepository = createInMemoryProjectRepository();
  const projectMemberRepository = createInMemoryProjectMemberRepository();
  const userRepository = createInMemoryUserRepository();
  const messageEventRepository = createInMemoryMessageEventRepository();
  const scenarioRepository = createInMemoryScenarioRepository();
  const vehicleRepository = createInMemoryVehicleRepository();
  const routeRepository = createInMemoryRouteRepository();

  projectRepository.seed(seedMockData.projects);
  projectMemberRepository.seed(seedMockData.projectMembers);
  messageEventRepository.seed(seedMockData.messageEvents);
  scenarioRepository.seed(seedMockData.scenarios);
  vehicleRepository.seed(seedMockData.vehicles);
  routeRepository.seed(seedMockData.routes);

  return {
    userRepository,
    projectRepository,
    projectMemberRepository,
    messageDefinitionRepository: createInMemoryMessageDefinitionRepository(),
    messageEventRepository,
    scenarioRepository,
    simulationTestRepository: createInMemorySimulationTestRepository(),
    simulationRunRepository: createInMemorySimulationRunRepository(),
    testSuiteRepository: createInMemoryTestSuiteRepository(),
    testCaseRepository: createInMemoryTestCaseRepository(),
    suiteResultRepository: createInMemorySuiteResultRepository(),
    connectionProfileRepository: createInMemoryConnectionProfileRepository(),
    reportRepository: createInMemoryReportRepository(),
    favoriteRepository: createInMemoryFavoriteRepository(),
    vehicleRepository,
    routeRepository,
    sessionRepository: createInMemorySessionRepository(),
    sessionEvidenceRepository: createInMemorySessionEvidenceRepository(),
    messageTemplateRepository: createInMemoryMessageTemplateRepository(),
    parameterSetRepository: createInMemoryParameterSetRepository(),
    datasetRepository: createInMemoryDatasetRepository(),
  };
};
